import React, { useState, useEffect, createRef, useCallback } from 'react';
import {
    ButtonField,
    ButtonFieldIcon,
    CheckboxField,
    DragAndDrop,
    FormFieldErrorInfo,
    Iconsvg,
    InputField,
    SegmentedControl,
    SegmentedControlItem,
    SelectField,
    Tag,
    TextareaField,
    Tooltip,
    TooltipBody,
    UploadStatus,
    WkSwitch
} from '@wk/components-react16';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toast } from '../../services';
import HttpService from "../../config/http.service";
import { API_URL } from "../../config/constants";
import ResponseTab from './ResponseTab';
import { RootState } from '../../store';
import { useSelector, useDispatch } from 'react-redux';
import { showLoading, hideLoading } from "../../store/authReducer";
import { encode } from 'gpt-tokenizer';
import { isValidJSON } from '../../utils';
import saveAs from 'file-saver';
import * as XLSX from "xlsx";
import { forkJoin, from } from 'rxjs';

const RunDocument = () => {
    const navigate = useNavigate();
    const location = useLocation();
	const dispatch = useDispatch();
    const { authProject } = useSelector((state: RootState) => state.auth);
    const stateData = location.state;
    const storyContainer = createRef<any>();
    let id = 0;
    const documentObj: any = {
        type: stateData ? stateData.promptType : null,
        prompt: stateData ? stateData.text : '',
        id: stateData ? stateData.id : 0,
        outputType: stateData ? stateData.jsonOutput : false
    };
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [promptList, setPromptList] = useState([] as any);
    const [promptFilterList, setPromptFilterList] = useState([] as any);
    const [promptTypeList, setPromptTypeList] = useState([] as any);
    const [fileList, setFileList] = useState([] as any);
    const [documentText, setDocumentText] = useState('');
    const [documentUrl, setDocumentUrl] = useState('');
    const [document, setDocument] = useState(documentObj);
    const [isSubmit, setIsSubmit] = useState(false);
    const [promptValidationList, setPromptValidationList] = useState([] as any);
    const [runPromptResponse, setRunPromptResponse] = useState([] as any);
    const [validationResponse, setValidationResponse] = useState({} as any);
    const [reqAuthoringValidationResponse, setReqAuthoringValidationResponse] = useState({} as any);
    const [validation, setValidation] = useState(false);
    const [inputOfRunPromptResponse, setInputOfRunPromptResponse] = useState({} as any);
    const [agentList, setAgentList] = useState<any[]>([]);
    const [selectedTestAgent, setSelectedTestAgent] = useState('');
    const handleBack = () => {
        navigate('/prompt');
    }

    const getPrompts = () => {
        HttpService.getRequest(`${API_URL.GET_PROMPT}?projectId=${authProject.id}`).then(
            (res: any) => {
                setPromptList(res.data ? res.data : []);
            },
            (err: any) => {
                Toast(err.message, "error");
            },
        )
    }

    const getPromptType = () => {
        HttpService.getRequest(`${API_URL.GET_PROMPT_TYPE_BY_PROJECT_ID_APPLICABLE}/${authProject.id}`).then(
            (res: any) => {
                setPromptTypeList(res);
            },
            (err: any) => {
                Toast(err.message, "error");
            },
        )
    }

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        if (name == 'type') {
            setPromptFilterList(promptList.filter((item: any) => item.promptType?.id == value));
            const selected = promptTypeList.find((item: any) => item.id == value);
            setDocument({
                ...document,
                [name]: selected ? selected : null,
                prompt: ''
            });
        } else if (name === 'testAgent') {
            setSelectedTestAgent(value);
        }  else {
            setDocument({
                ...document,
                [name]: value
            });
        }
    };

    const handlePromptChange = (event: any) => {
        const { name, value } = event.target;
        const selected = promptList.find((item: any) => item.id == value);
        setValidation(false);
        if (name == 'prompt' && selected && selected.validations?.length > 0) {
            setPromptValidationList(selected.validations);
        } else {
            setPromptValidationList([]);
        }
        setDocument({
            ...document,
            id: selected ? value : null,
            [name]: selected ? selected.text : '',
        });
    };

    const isValidUrl = (url: string) => {
        try {
            const urlPattern = /^(https?:\/\/)([\w\d\-]+\.)+[\w\d\-]+(\/[^\s]*)?$/i;
            return urlPattern.test(url);
        } catch (error) {
            return false;
        }
    }

    const isValidInput = (doc: any) => {
        if (!doc.type || !doc.type?.code.trim()) {
            Toast('Please select prompt type', "error");
            return false;
        }
        if (!doc.prompt || !doc.prompt.trim()) {
            Toast('Please select prompt', "error");
            return false;
        }
        return true;
    }

    const runDocument = async () => {
        if (!isValidInput(document)) return;
        if (selectedIndex == 0) {
            if (!documentText || !documentText.trim()) {
                Toast('Please enter document text', "error");
                return;
            }
        }
        if (selectedIndex == 1) {
            if (fileList.length == 0) {
                Toast('Please upload document', "error");
                return;
            }
        }
        if (selectedIndex == 2) {
            if (!documentUrl || !documentUrl.trim()) {
                Toast('Please enter document url', "error");
                return false;
            }
            if (!isValidUrl(documentUrl)) {
                Toast('Please enter a valid URL', "error");
                return false;
            }
        }
        const formData = new FormData();
        formData.append('type', document.type?.code);
        formData.append('fab_agent', selectedTestAgent ? selectedTestAgent :  document.type?.gptAgent || null);
        formData.append('prompt', document.prompt);
        formData.append('outputType', document.outputType);
        formData.append('documentText', documentText);
        formData.append('documentUrl', documentUrl);
        formData.append('projectId', authProject?.id);
        if (fileList.length > 0) {
            for (let file of fileList) {
                formData.append('document', file.nativeFile);
            }
        }

        // Capture input data for export
		const inputData: any = {
			type: document.type?.code,
			prompt: document.prompt,
			outputType: document.outputType,
			documentText: documentText,
			documentUrl: documentUrl
		};
		
		if (fileList.length > 0) {
			const fileNames = [];
			for (let file of fileList) {
				fileNames.push(file.nativeFile.name);
			}
			inputData.documents = fileNames;
		}
		
		setInputOfRunPromptResponse(inputData);

        setIsSubmit(true);
        dispatch(showLoading());
        HttpService.postRequestFormData(API_URL.RUN_DOCUMENT, formData).then(
            (res: any) => {
                if (res.data && res.data[0]?.status == 'success') {
                    setRunPromptResponse(res.data ? res.data.map((item: any) => ({ ...item, isCopy: false})) : []);
                    if (validation){
                        runValidation(res.data[0]?.response);
                    } else {
                        setValidationResponse({});
                    }
                } else {
                    setRunPromptResponse([{
                        documentName: "",
                        response: "",
                        rationale: "",
                        prompt_score: 0
                    }]);
                }
                setIsSubmit(false);
                if (!validation) {
                    dispatch(hideLoading());
                }
            },
            (err: any) => {
                setRunPromptResponse({
                    documentName: "",
                    response: "",
                    rationale: "",
                    prompt_score: 0
                });
                Toast(err.message, "error");
                dispatch(hideLoading());
                setIsSubmit(false);
            }
        )
    }

    const runValidation = async (data: any) => {
        setValidationResponse({});
        if (!promptValidationList || promptValidationList.length == 0) {
            Toast('No Validation cases added to this prompt', "error");
            return;
        }
        setIsSubmit(true);
        const getValidationData = (res: any) => (res?.status == 200 && res?.data ? res.data : res);

        if (document.type?.name == 'Requirement Authoring') {
            const parsedData = typeof data === 'object' ? data : (typeof data === 'string' && isValidJSON(data) ? JSON.parse(data) : null);
            const requirements = parsedData && Array.isArray(parsedData.requirements) ? parsedData.requirements : [];

            if (requirements.length > 0) {
                const validationRequests: any[] = requirements.map((requirement: any) =>
                    from(HttpService.postRequest(API_URL.RUN_VALIDATION, {
                        llmResponse: JSON.stringify(requirement),
                        validationCases: promptValidationList,
                        promptInstructions: document.prompt,
                    }))
                );

                forkJoin(validationRequests).subscribe(
                    (responses: any[]) => {
                        const mappedValidationResponses = responses.map((response: any, index: number) => {
                            const requirement = requirements[index];
                            const requirementTitle = requirement?.title || `Requirement ${index + 1}`;
                            return {
                                requirementTitle,
                                validationResponse: getValidationData(response)
                            };
                        });

                        setReqAuthoringValidationResponse(mappedValidationResponses);
                        setIsSubmit(false);
                        dispatch(hideLoading());
                    },
                    (err: any) => {
                        Toast(err.message, "error");
                        dispatch(hideLoading());
                        setIsSubmit(false);
                    }
                );
                return;
            }
        }

        const payload = {
            llmResponse: data,
            validationCases: promptValidationList,
            promptInstructions: document.prompt
        }

        // dispatch(showLoading());
        HttpService.postRequest(API_URL.RUN_VALIDATION, payload).then(
            (res: any) => {
                const responseData = getValidationData(res);
                if (responseData) {
                    setValidationResponse(responseData);
                }
                setIsSubmit(false);
                dispatch(hideLoading());
            },
            (err: any) => {
                Toast(err.message, "error");
                dispatch(hideLoading());
                setIsSubmit(false);
            }
        )
    }

    const handleReset = () => {
        setDocument({
            type: null,
            prompt: '',
            id: 0,
            outputType: false
        });
        setFileList([]);
        setDocumentText('');
        setRunPromptResponse([]);
        setValidationResponse({});
        setInputOfRunPromptResponse({});
        setPromptValidationList([]);
        setDocumentUrl('');
        setValidation(false);
        dispatch(hideLoading());
        setIsSubmit(false);
        setSelectedTestAgent('');
    }

    const handleSegmentChange = (event: any) => {
        setSelectedIndex(event);
        if (event == 0) {
            setFileList([]);
            setDocumentUrl('');
        } else if   (event == 1) {
            setDocumentText('');
            setDocumentUrl('');
        } else if (event == 2) {
            setDocumentText('');
            setFileList([]);
        }
    }

    const handleFilesChange = (event: any) => {
        const targetFiles = Array.isArray(event) ? event : Array.from(event.target.files);
        const newFileItems = targetFiles.map((file: File) => {
            const uiFile = {
                id: _nextId(),
                nativeFile: file,
                label: file.name,
                type: file.type,
                progress: 100,
            };
            return {
                ...uiFile,
                status: 'completed',
            };
        });

        const newFileList = [...fileList];
        setFileList([...newFileItems, ...newFileList]);
    }

    const handleFileItemClick = (file: any) => {
        if (file.status !== 'uploading') {
            setFileList(fileList.filter((f: any) => f.id !== file.id));
            const nativeInput =
                storyContainer.current.ownerDocument.querySelector(
                    'input[type="file"]'
                ) as HTMLInputElement;
            nativeInput.value = '';
        }
    }

    const _nextId = () => {
        id += 1;
        return 'i-' + id;
    }

    const getErrors = (): Array<FormFieldErrorInfo> => {
        return fileList.filter((f: any) => f.errorMsg).map((f: any) => ({ label: f.errorMsg, labelId: f.id }));
    }

    const getErrorIds = () => {
        return getErrors().map((e: any) => e.labelId).join(',');
    }

    const getIconName = (file: any) => {
        if (file.type.includes('pdf')) {
            return 'file-pdf';
        } else if (file.type.includes('jpeg') || file.type.includes('png')) {
            return 'photo';
        } else {
            return 'file';
        }
    }

    const exportPromptOutput = () => {
		if (runPromptResponse?.data?.length == 0) {
			Toast('No prompt output available to export', "error");
			return;
		}
		
		try {
			const exportData = {
				input: inputOfRunPromptResponse,
				output: runPromptResponse
			};
			
			const jsonString = JSON.stringify(exportData, null, 2);
			const blob = new Blob([jsonString], { type: 'application/json' });
			const url = URL.createObjectURL(blob);
			
			const link = window.document.createElement('a');
			link.href = url;
			link.download = `Prompt_Output_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
			link.style.display = 'none';
			window.document.body.appendChild(link);
			link.click();
			
			// Cleanup
			setTimeout(() => {
				window.document.body.removeChild(link);
				URL.revokeObjectURL(url);
			}, 100);
			
			Toast('Prompt output exported successfully', 'success');
		} catch (error: any) {
			Toast('Error exporting file: ' + error.message, 'error');
		}
	}

    useEffect(() => {
        if (authProject && authProject.id) {
            getPrompts();
            getPromptType();
        }
    }, [authProject]);

    useEffect(() => {
        if (stateData && stateData.promptType) {
            setPromptFilterList(promptList.filter((item: any) => item.promptType?.id == stateData.promptType?.id));
        }
        if (stateData && stateData.validations?.length > 0) {
            setPromptValidationList(stateData.validations);
        } else {
            setPromptValidationList([]);
        }
    }, [stateData, promptList]);
    
    const exportPromptOutputExcel = () => {
            if(isValidJSON(runPromptResponse[0].response)) {
                let responseData = JSON.parse(runPromptResponse[0].response);
    
                if(document.type?.name === 'Requirement Authoring') {
                    responseData = responseData.requirements;
                    if (!responseData) {
                        Toast('No prompt output available to export', "error");
                        return;
                    }
                    //flatten the response data for Excel export
                    const flattenedData = responseData.map((item: any) => ({
                        ...item,
                        subthemeTitle: item.subtheme?.title,
                        subthemeSummary: item.subtheme?.summary,
                        subthemeRationale: item.subtheme?.rationale
                    }));
        
                    flattenedData.forEach((item: any) => delete item.subtheme); // removed flattened object to avoid confusion in excel
                    exportToExcel(flattenedData);
                } else if (document.type?.name === 'Requirement Clustering') {
                    responseData = responseData.clusters;
                    if (!responseData) {
                        Toast('No prompt output available to export', "error");
                        return;
                    }
                    //flatten the response data for Excel export
                    const flattenedData = responseData.flatMap((cluster:any) =>
                        cluster.requirements.map((req:any) => ({
                            id: cluster.id,
                            name: cluster.name,
                            summary: cluster.summary,
                            rationale: cluster.rationale,
                            requirementId: req.id,
                            requirementTitle: req.title,
                            status: cluster.status
                        }))
                    );
        
                    flattenedData.forEach((item: any) => delete item.requirements); // removed flattened object to avoid confusion in excel
                    exportToExcel(flattenedData);
                } else if (document.type?.name === 'Tagging') {
                    responseData = responseData.tags;
                    if (!responseData) {
                        Toast('No prompt output available to export', "error");
                        return;
                    }
    
                    exportToExcel(responseData);
    
                }
    
    
            } else {
                Toast('Prompt output is not in valid JSON format, cannot export to Excel', "error");
                return;
            }
        }
        const exportToExcel = (jsonData:any) => {
            const worksheet = XLSX.utils.json_to_sheet(jsonData);
    
            const workbook = {
                Sheets: { data: worksheet },
                SheetNames: ["data"]
            };
    
            const excelBuffer = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array"
            });
    
            const fileData = new Blob([excelBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
    
            saveAs(fileData, "Prompt_Output.xlsx");
        };

        const getAgentList = useCallback(() => {
                dispatch(showLoading());
                HttpService.getRequest(`${API_URL.GET_AGENT_LIST}`).then(
                    (res: any) => {
                            setAgentList(res ? res : []);
                            dispatch(hideLoading());
                        },
                        (err: any) => {
                            Toast(err.message, "error")
                            dispatch(hideLoading());
                    },
                )
        }, []);
    
        useEffect(() => {
            getAgentList();
        }, []);
        
        
    return (
        <div data-component="RunDocument">
            <div className={`pl-20 ${runPromptResponse.length > 0 ? 'mt-20' : 'card-maxwidth'}`}>
                <ButtonField iconPosition={'left'} mode={'ghost'} size={'small'}>
                    <button
                        type="button"
                        data-e2e="cg-previous-button"
                        onClick={handleBack}>
                        <Iconsvg name="arrow-left"></Iconsvg>
                        Back to Prompt Library{' '}
                    </button>
                </ButtonField>
            </div>
            <div className="wk-row m-0">
                <div className={`pl-20 ${runPromptResponse.length > 0 ? 'wk-col-5' : 'wk-col-12 pr-10'}`}>
                    <div className={`p-20 custom-fs-field mb-0 card-primary fade-in ${runPromptResponse.length > 0 ? 'mt-20' : 'card-maxwidth'}`}>
                        <h4 className='mb-20'>Run Document</h4>
                        <SelectField label="Prompt Type" labelFor="select-id-1" indicator="required">
                            <select id="select-id-1" name="type"
                                onChange={(event: any) => handleInputChange(event)}
                                value={document.type?.id || '0'}>
                                <option value="0">-- Select Prompt Type --</option>
                                {promptTypeList.map((item: any, i: number) => <option value={item.id} key={i}>{item.name}</option>)}
                            </select>
                        </SelectField>
                        <div className="flex">
                            <p className='mr-15'><span style={{ fontSize: '0.875rem', fontWeight: '500'  }}>Assigned FAB Agent: </span>
                                <span style={{textDecoration: selectedTestAgent ? 'line-through' : 'none' }}>{document.type?.gptAgent}</span>
                            </p>
                            <SelectField size='small'>
                                <select
                                    name="testAgent"
                                    value={selectedTestAgent || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">-- Select Test Agent --</option>
                                    {agentList.map((item: any, i: number) => <option value={item.name} key={i}>{item.name}</option>)}
                                </select>
                            </SelectField>
                        </div>
                        <SelectField label="Prompt" labelFor="select-id-3" indicator="required">
                            <select id="select-id-3" name="prompt" value={document.id || '0'}
                                onChange={(event: any) => handlePromptChange(event)}
                                >
                                <option value="0">-- Select Prompt --</option>
                                {promptFilterList.map((item: any, i: number) => <option value={item.id} key={i}>{item.name}</option>)}
                            </select>
                        </SelectField>
                        <div className="position-relative">
                            <div style={{ fontSize: '12px', color: encode(document.prompt || '').length > 1200 ? '#E5202E' : '#777' }} className='position-absolute top-0 end-0'>{encode(document.prompt || '').length}/ 1200 tokens 
                                <span className="ml-10" id='info-icon'><Iconsvg name="info" size={16} ></Iconsvg></span>
                                <Tooltip
                                    targetSelector="#info-icon"
                                    identifier="tooltip-top"
                                    position="top"
                                    >
                                    <TooltipBody slot="tooltipBody">Staying within ~1200 tokens helps the AI follow instructions more accurately. This is only a guideline; your prompt will still work beyond this.</TooltipBody>
                                </Tooltip>
                            </div>
                            <TextareaField label="Prompt Instructions" labelFor="textarea-id-1" indicator="required">
                                <textarea style={{ height: '10rem' }}
                                    id="textarea-id-1" name="prompt"
                                    placeholder="Prompt Instructions"
                                    onChange={(event: any) => handleInputChange(event)}
                                    value={document.prompt}
                                ></textarea>
                            </TextareaField>
                        </div>
                        <div className='mb-15'>
                            <WkSwitch
                                name="outputType" isChecked={document.outputType}
                                srLabel="Json Output"
                                label="Json Output"
                                onSwitch={(event: any) => setDocument({
                                    ...document,
                                    outputType: event,
                                })}></WkSwitch>
                        </div>
                        <div className="mb-15 mt-5" ref={storyContainer}>
                            <div className="cg-field-header" style={{ fontSize: '0.875rem' }}>
                                <label className="cg-field-label">Documents
                                    <div className="cg-field-required-indicator"><span aria-hidden="true">*</span></div>
                                </label>
                            </div>
                            <div className='custom-segmented-control'>
                                <SegmentedControl
                                    selectedIndex={selectedIndex}
                                    controlMode={'controlled'}
                                    onUserRequest={(event: any) => handleSegmentChange(event)}>
                                    <SegmentedControlItem slot="segmentedControlItem">
                                        Document Text
                                    </SegmentedControlItem>
                                    <SegmentedControlItem slot="segmentedControlItem">
                                        Document Upload
                                    </SegmentedControlItem>
                                    <SegmentedControlItem slot="segmentedControlItem">
                                        Document Url
                                    </SegmentedControlItem>
                                </SegmentedControl>
                            </div>
                            {selectedIndex == 0 ? (
                                <div className="mb-15 mt-5">
                                    <TextareaField labelFor="textarea-id-3">
                                        <textarea style={{ height: '10rem' }}
                                            id="textarea-id-3" name="documentText"
                                            placeholder="Document Text"
                                            onChange={(event: any) => setDocumentText(event.target.value)}
                                            value={documentText}
                                        ></textarea>
                                    </TextareaField>
                                </div>
                            ) : null}
                            { selectedIndex == 1 ? (
                                <div className="mb-15 mt-5" ref={storyContainer}>
                                    <DragAndDrop
                                        acceptTypes=".xlsx,.docx,.json,.png,.jpg,.jpeg" size="medium"
                                            onFilesChange={(event: any) => handleFilesChange(event)}>
                                        <input
                                            id="file-upload-001"
                                            type="file"
                                            name="file-upload"
                                            multiple
                                            accept=".xlsx,.docx,.json,.png,.jpg,.jpeg"
                                            aria-describedby={getErrorIds()}
                                            onChange={(event: any) => handleFilesChange(event)}
                                            slot="dragAndDropInput"
                                        />
                                        {fileList.map((item: any, i: number) =>
                                            <UploadStatus
                                                key={i}
                                                label={item.label}
                                                status={item.status}
                                                size={item.nativeFile.size}
                                                progress={item.progress}
                                                isCompact
                                                onItemClick={() => handleFileItemClick(item)}
                                                slot="dragAndDropListItem">
                                                <Iconsvg name={getIconName(item)}></Iconsvg>
                                            </UploadStatus>
                                        )}
                                    </DragAndDrop>
                                    <div className='mt-5 wk-size-1 text-warning-emphasis'>Accepted formats: Excel, Word (DOCX), JSON, PNG, JPG/JPEG.</div>
                                </div>
                            ) :  null}
                            {selectedIndex == 2 ? (
                                <div className="mb-15 mt-5">
                                    <InputField labelFor="input-id-1-3" indicator="required">
                                        <input
                                            type="text" id="input-id-1-3" name="documentUrl"
                                            placeholder="Document Url"
                                            onChange={(event: any) => setDocumentUrl(event.target.value)}
                                            value={documentUrl} autoComplete="off"
                                        />
                                    </InputField>
                                </div>
                            ) : null}
                        </div>
                        <div className='mb-15 mt-10 custom-fs-field'>
                            <CheckboxField label="Run Test Validation">
                                <input
                                    name="checkbox1"
                                    type="checkbox"
                                    disabled={promptValidationList.length == 0}
                                    checked={validation}
                                    onChange={(event: any) => setValidation(event.target.checked)}
                                />
                            </CheckboxField>
                        </div>
                        <div className='text-right'>
                            <ButtonField mode={'ghost'}>
                                <button className="mr-10" type="button" onClick={() => handleReset()}>Reset</button>
                            </ButtonField>
                            <ButtonField>
                                <button type="button" disabled={isSubmit} onClick={() => runDocument()}>Submit</button>
                            </ButtonField>
                        </div>
                    </div>
                </div>
                {runPromptResponse.length > 0 ? (
                    <div className="wk-col-7 pl-0 pr-0 fade-in">
                        <div className="p-20">
                            <div className="text-right">
                                {(document.type?.name === 'Requirement Authoring' || document.type?.name === 'Requirement Clustering' || document.type?.name === 'Tagging') ? (
                                <>
                                    <ButtonFieldIcon>
                                        <button id='export-excel-output' type="button" onClick={() => exportPromptOutputExcel()}>
                                            <Iconsvg name="file-excel" color='#020102ff'></Iconsvg>
                                        </button>
                                    </ButtonFieldIcon>
                                    <Tooltip
                                        targetSelector="#export-excel-output"
                                        identifier="tooltip-top"
                                        position="top"
                                        >
                                        <TooltipBody slot="tooltipBody">Export Prompt Output Excel</TooltipBody>
                                    </Tooltip>
                                </>
                                    
                                ) : null}
                                <ButtonFieldIcon>
                                <button id='export-prompt-output' type="button" onClick={() => exportPromptOutput()}>
                                    <Iconsvg name="export" color='#020102ff'></Iconsvg>
                                </button>
                                </ButtonFieldIcon>
                                <Tooltip
                                    targetSelector="#export-prompt-output"
                                    identifier="tooltip-top"
                                    position="top"
                                    >
                                    <TooltipBody slot="tooltipBody">Export Prompt Output JSON</TooltipBody>
                                </Tooltip>
                            </div>
                            <ResponseTab data={{ runPromptResponse, setRunPromptResponse, validationResponse, promptType: document.type?.name, reqAuthoringValidationResponse, promptId: document.id, setPromptValidationList }} />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default RunDocument